import { ApolloError } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";

/**
 * Marker key written onto `extensions` by {@link createExpectedErrorsLink} when
 * it promotes a backend `expectedErrors` entry to a top-level GraphQL error.
 * The error link reads it to tell an *expected* error (already reported by the
 * BE with a traceId) apart from an *unexpected* one it must report itself.
 */
export const EXPECTED_ERROR_EXTENSION = "expectedError" as const;

/**
 * Normalized expected error. Mirrors the backend `UserError` / `expectedErrors`
 * shape (`code`, `message`, `traceId`) plus optional confirmation prompts for
 * the `REQUIRES_CONFIRMATION` flow. Nullable wire values are normalized to
 * `undefined` here so consumers don't juggle `null` vs `undefined`.
 */
export type ExpectedError = {
  code?: string;
  message: string;
  traceId?: string;
  confirmations?: unknown[];
};

/** Anything carrying the `graphQLErrors` array — an `ApolloError` or a subset of it. */
type HasGraphQLErrors = Pick<ApolloError, "graphQLErrors">;

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function toExpectedError(error: GraphQLFormattedError): ExpectedError {
  const extensions = error.extensions ?? {};
  return {
    message: error.message,
    code: readString(extensions["code"]),
    traceId: readString(extensions["traceId"]),
    confirmations: Array.isArray(extensions["confirmations"])
      ? (extensions["confirmations"] as unknown[])
      : undefined,
  };
}

/** True when an error was promoted from a backend `expectedErrors` entry. */
export function isExpectedError(error: GraphQLFormattedError): boolean {
  return error.extensions?.[EXPECTED_ERROR_EXTENSION] === true;
}

/**
 * Extracts the typed expected errors from a thrown `ApolloError`. This is the
 * Phase 3 replacement for string-matching `error.message` or checking union
 * `__typename`s in containers.
 */
export function getExpectedErrors(error: HasGraphQLErrors): ExpectedError[] {
  return (error.graphQLErrors ?? []).filter(isExpectedError).map(toExpectedError);
}

/** First expected error code on the error, or `undefined`. */
export function getErrorCode(error: HasGraphQLErrors): string | undefined {
  return getExpectedErrors(error)[0]?.code;
}

/**
 * First Sentry `traceId` found on any GraphQL error (expected or not), for
 * cross-stack correlation. The BE attaches it to errors it reports.
 */
export function getTraceId(error: HasGraphQLErrors): string | undefined {
  for (const graphQLError of error.graphQLErrors ?? []) {
    const traceId = readString(graphQLError.extensions?.["traceId"]);
    if (traceId) {
      return traceId;
    }
  }
  return undefined;
}

/**
 * Best-effort human-readable message for any thrown value, with a fallback.
 *
 * Prefers the first GraphQL error's message — where {@link createExpectedErrorsLink}
 * surfaces promoted backend `expectedErrors` — then the network error, then a
 * generic `Error.message`, and finally `fallback`. Use in `catch` blocks to
 * drive toast/UX copy.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApolloError) {
    const firstGraphQLError = error.graphQLErrors[0];
    if (firstGraphQLError) return firstGraphQLError.message;
    if (error.networkError) return error.networkError.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
